import { Box, IconButton } from '@mui/material';
import { ConstructionIcon, XIcon } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import {
  buildMaintenanceReminderCopy,
  buildMaintenanceReminderTitle,
  MAINTENANCE_ACKNOWLEDGE_LABEL,
  MAINTENANCE_REMINDER_DISMISS_LABEL,
} from '@/constants/Maintenance/maintenance';
import { corePalette } from '@/themes/colours';
import { formatMaintenanceTime } from '@/utils/functions/maintenance';
import CustomButton from '../Common/Button';
import CustomTypography from '../Common/Typography';
import MaintenanceCopy from './MaintenanceCopy';

interface IProps {
  isOpen: boolean;
  startsAt: string;
  expectedEndAt: string;
  leadMinutes: number;
  onDismiss: () => void;
}

export default function useMaintenanceReminderSnackbar({
  isOpen,
  startsAt,
  expectedEndAt,
  leadMinutes,
  onDismiss,
}: IProps): void {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (!isOpen) return undefined;

    const id = enqueueSnackbar(
      (
        <Box display="flex" flexDirection="column" gap="8px" width="100%">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap="8px"
          >
            <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
              <ConstructionIcon
                size={24}
                color={corePalette.yellow100}
                style={{ flexShrink: 0 }}
              />
              <CustomTypography
                component="span"
                variant="bodyRegular"
                fontWeight={500}
                sx={{ color: corePalette.white }}
              >
                {buildMaintenanceReminderTitle(leadMinutes)}
              </CustomTypography>
            </Box>
            <IconButton
              onClick={onDismiss}
              aria-label={MAINTENANCE_REMINDER_DISMISS_LABEL}
              sx={{ padding: '4px', color: corePalette.white }}
            >
              <XIcon size={24} />
            </IconButton>
          </Box>

          <CustomTypography
            component="span"
            variant="bodySmall"
            sx={{ color: corePalette.white }}
          >
            <MaintenanceCopy
              segments={buildMaintenanceReminderCopy({
                start: formatMaintenanceTime(startsAt),
                end: formatMaintenanceTime(expectedEndAt),
                leadMinutes,
              })}
            />
          </CustomTypography>
        </Box>
      ),
      {
        variant: 'warning',
        persist: true,
        hideIconVariant: true,
        action: (
          <CustomButton
            size="small"
            variant="bold"
            label={MAINTENANCE_ACKNOWLEDGE_LABEL}
            onClick={onDismiss}
          />
        ),
      },
    );

    return () => closeSnackbar(id);
  }, [
    isOpen,
    startsAt,
    expectedEndAt,
    leadMinutes,
    onDismiss,
    enqueueSnackbar,
    closeSnackbar,
  ]);
}
